// Lightweight client-side attribution helper.
// Captures UTM parameters and stores them for the current browser session so
// even if the user navigates around before submitting the form, we still know
// where they originally came from.

const STORAGE_KEY = "ataiwo_attribution_v1";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"];

function readStored() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

function writeStored(data) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (_) { /* ignore */ }
}

/**
 * Call once on app load (in App.js). Captures UTM/gclid from the URL if present.
 */
export function captureAttribution() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) utm[k] = v;
    }
    if (Object.keys(utm).length === 0) return;
    // Only overwrite if this session doesn't have UTM yet OR page load has new params
    const existing = readStored();
    if (!existing || !existing.utm || Object.keys(existing.utm).length === 0) {
      writeStored({
        utm,
        referrer: document.referrer || "",
        first_seen: new Date().toISOString(),
        landing_url: window.location.href,
      });
    }
  } catch (_) { /* ignore */ }
}

function deviceTypeFromUA(ua) {
  if (!ua) return "unknown";
  const lower = ua.toLowerCase();
  if (/tablet|ipad/.test(lower)) return "tablet";
  if (/mobile|android|iphone|ipod/.test(lower)) return "mobile";
  return "desktop";
}

/**
 * Get the current tracking context to send with a form submission.
 */
export function getTrackingContext() {
  if (typeof window === "undefined") return {};
  const stored = readStored() || {};
  const ua = navigator.userAgent || "";
  const ctx = {
    referrer: stored.referrer || document.referrer || "direct",
    landing_url: stored.landing_url || window.location.href,
    utm_source: stored.utm?.utm_source || "",
    utm_medium: stored.utm?.utm_medium || "",
    utm_campaign: stored.utm?.utm_campaign || "",
    utm_term: stored.utm?.utm_term || "",
    utm_content: stored.utm?.utm_content || "",
    gclid: stored.utm?.gclid || "",
    device_type: deviceTypeFromUA(ua),
    user_agent: ua.substring(0, 250),
    language: (navigator.language || "").substring(0, 20),
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
  };
  return ctx;
}

/**
 * Human-readable source label (e.g., "Google Ads", "WhatsApp", "Direct").
 */
export function labelSource(quote) {
  if (!quote) return "Onbekend";
  if (quote.gclid || (quote.utm_source === "google" && quote.utm_medium === "cpc")) return "Google Ads";
  if (quote.utm_source) {
    return `${quote.utm_source}${quote.utm_medium ? " (" + quote.utm_medium + ")" : ""}`;
  }
  const ref = (quote.referrer || "").toLowerCase();
  if (!ref || ref === "direct") return "Direct";
  if (ref.includes("google")) return "Google (organisch)";
  if (ref.includes("bing")) return "Bing";
  if (ref.includes("whatsapp")) return "WhatsApp";
  if (ref.includes("facebook")) return "Facebook";
  if (ref.includes("instagram")) return "Instagram";
  if (ref.includes("linkedin")) return "LinkedIn";
  if (ref.includes("trustoo")) return "Trustoo";
  if (ref.includes("werkspot")) return "Werkspot";
  try {
    const url = new URL(quote.referrer);
    return url.hostname.replace(/^www\./, "");
  } catch (_) { return "Extern"; }
}
